import navigationRoutes, { type INavigationRoute } from '../components/sidebar/NavigationRoutes'
import type { TypeUserPermission } from '../types/User'
import { DASHBOARD_KEYS, DASHBOARD_ROUTE_MAP, FALLBACK_DASHBOARD_KEY, parseDashboardKey } from './defaultDashboard'

export type ModuleAccessCheck = (slug: string) => boolean
export type PermissionAccessCheck = (action: keyof TypeUserPermission, subject: string) => boolean

export type RouteAccessRule = {
  /** Sidebar section module slug(s). Any match. */
  moduleGate?: string | string[]
  /** Sidebar leaf permission name(s). Any match. Omitted for module-only items. */
  permissionGate?: string | string[]
}

/**
 * Same parent/leaf gate rules as the sidebar: section headers use module slugs,
 * leaf items use permission names (`read`, or `create` when the gate starts with `create_`).
 */
export function userMeetsNavGate(
  gate: string | string[] | undefined,
  isParent: boolean,
  hasModule: ModuleAccessCheck,
  hasPermission: PermissionAccessCheck,
): boolean {
  if (gate == null) {
    return true
  }

  if (isParent) {
    const slugs = Array.isArray(gate) ? gate : [gate]
    return slugs.some((slug) => hasModule(slug))
  }

  if (Array.isArray(gate)) {
    return gate.some((subject) => hasPermission('read', subject))
  }

  if (gate.startsWith('create_')) {
    const subject = gate.split('_')[1]
    return Boolean(subject) && hasPermission('create', subject)
  }

  return hasPermission('read', gate)
}

function assignRule(map: Map<string, RouteAccessRule>, routeName: string, rule: RouteAccessRule) {
  if (!routeName || map.has(routeName)) {
    return
  }
  map.set(routeName, rule)
}

function collectNavAccessRules(routes: INavigationRoute[]): Map<string, RouteAccessRule> {
  const map = new Map<string, RouteAccessRule>()

  const register = (routeName: string, rule: RouteAccessRule, activeRoutes?: string[]) => {
    assignRule(map, routeName, rule)
    for (const extraName of activeRoutes ?? []) {
      assignRule(map, extraName, rule)
    }
  }

  for (const section of routes) {
    if (!section.children?.length) {
      register(section.name, { moduleGate: section.gate }, section.activeRoutes)
      continue
    }

    for (const child of section.children) {
      register(
        child.name,
        {
          moduleGate: section.gate,
          permissionGate: child.gate,
        },
        child.activeRoutes,
      )
    }
  }

  return map
}

/**
 * Named routes that are not sidebar leaves but open the same page as one that is.
 * `locations` is the legacy `/setup/locations` URL for inventory locations.
 */
const ROUTE_ACCESS_ALIASES: Record<string, string> = {
  locations: 'inventory-locations',
}

/**
 * Account pages the profile menu opens for every signed-in user.
 * Preferences sits under Settings in the sidebar (navigation grouping) but
 * does not require the settings module — cashiers still change theme and password.
 */
const UNGATED_ACCOUNT_ROUTES = new Set(['preferences'])

const navAccessRules = collectNavAccessRules(navigationRoutes.routes)

export function routeAccessRuleFor(routeName: string): RouteAccessRule | null {
  const mappedName = ROUTE_ACCESS_ALIASES[routeName] ?? routeName
  if (UNGATED_ACCOUNT_ROUTES.has(mappedName)) {
    return null
  }
  return navAccessRules.get(mappedName) ?? null
}

/** True when the route is ungated, or the user meets the sidebar module and permission gates. */
export function canAccessNamedRoute(
  routeName: string,
  hasModule: ModuleAccessCheck,
  hasPermission: PermissionAccessCheck,
): boolean {
  const rule = routeAccessRuleFor(routeName)
  if (!rule) {
    return true
  }

  return (
    userMeetsNavGate(rule.moduleGate, true, hasModule, hasPermission) &&
    userMeetsNavGate(rule.permissionGate, false, hasModule, hasPermission)
  )
}

/**
 * Named Vue route the user may land on after login or context selection.
 *
 * Preferred key wins only when that sidebar-gated page is already accessible.
 * Otherwise the first authorized dashboard in enum order, then General.
 */
export function resolveAccessibleLandingRoute(
  preferredKey: unknown,
  hasModule: ModuleAccessCheck,
  hasPermission: PermissionAccessCheck,
): string {
  const preferred = parseDashboardKey(preferredKey)
  const keys = preferred ? [preferred, ...DASHBOARD_KEYS.filter((key) => key !== preferred)] : DASHBOARD_KEYS

  for (const key of keys) {
    const routeName = DASHBOARD_ROUTE_MAP[key].routeName
    if (canAccessNamedRoute(routeName, hasModule, hasPermission)) {
      return routeName
    }
  }

  return DASHBOARD_ROUTE_MAP[FALLBACK_DASHBOARD_KEY].routeName
}

/**
 * Redirect when the destination is a sidebar-gated page the user cannot open.
 * Ungated routes and the last-resort general dashboard stay put so navigation cannot loop.
 */
export function resolveUnauthorizedRouteRedirect(
  routeName: string,
  preferredKey: unknown,
  hasModule: ModuleAccessCheck,
  hasPermission: PermissionAccessCheck,
): string | null {
  if (canAccessNamedRoute(routeName, hasModule, hasPermission)) {
    return null
  }

  const fallback = resolveAccessibleLandingRoute(preferredKey, hasModule, hasPermission)
  return fallback !== routeName ? fallback : null
}
